import { TemplateValidationError } from "../errors";
import type { EngineWarning } from "../types/render";
import type {
  LabelTemplateV2,
  TemplateVariableDefinition,
  TemplateVariableType,
} from "../types/template";
import { assertNonNegativeSpacing, normalizeSpacing } from "../utils/spacing";
import {
  validateBarcodeDefaults,
  validateElement,
  validateFontDefaults,
} from "./elementValidators";
import { assertFinite, assertPositive } from "./helpers";

export interface TemplateValidationResult {
  requiredVariables: string[];
  warnings: EngineWarning[];
}

function isValidVariableType(value: unknown): value is TemplateVariableType {
  return value === "string" || value === "number";
}

function isValidVariablePath(path: string): boolean {
  if (path.includes("[") || path.includes("]")) {
    return false;
  }

  return /^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z_][A-Za-z0-9_]*)*$/.test(path);
}

function validateVariables(
  variables: TemplateVariableDefinition[],
  issues: string[],
): {
  declaredPaths: Set<string>;
  requiredDeclaredPaths: Set<string>;
} {
  const declaredPaths = new Set<string>();
  const requiredDeclaredPaths = new Set<string>();

  variables.forEach((variable, index) => {
    const variablePath = `variables[${index}]`;

    if (
      typeof variable.path !== "string" ||
      variable.path.trim().length === 0
    ) {
      issues.push(`${variablePath}.path must be a non-empty string.`);
      return;
    }

    if (!isValidVariablePath(variable.path)) {
      issues.push(
        `${variablePath}.path must be dot notation without array syntax (e.g. 'sample.id').`,
      );
    }

    if (declaredPaths.has(variable.path)) {
      issues.push(`${variablePath}.path '${variable.path}' must be unique.`);
    }
    declaredPaths.add(variable.path);

    if (!isValidVariableType(variable.type)) {
      issues.push(`${variablePath}.type must be 'string' or 'number'.`);
    }

    if (
      variable.required !== undefined &&
      typeof variable.required !== "boolean"
    ) {
      issues.push(`${variablePath}.required must be a boolean when provided.`);
    }

    if (variable.required === true) {
      requiredDeclaredPaths.add(variable.path);
    }
  });

  return { declaredPaths, requiredDeclaredPaths };
}

function validateContainers(
  template: LabelTemplateV2,
  referencedVariablePaths: Set<string>,
  requiredSourcePaths: Set<string>,
  warnings: EngineWarning[],
  issues: string[],
): void {
  const usedContainerIds = new Set<string>();
  const usedElementIds = new Set<string>();

  template.containers.forEach((container, containerIndex) => {
    const containerPath = `containers[${containerIndex}]`;

    if (usedContainerIds.has(container.id)) {
      issues.push(
        `${containerPath}.id '${container.id}' must be unique across containers.`,
      );
    }
    usedContainerIds.add(container.id);

    assertPositive(container.widthMm, `${containerPath}.widthMm`, issues, true);
    assertPositive(
      container.heightMm,
      `${containerPath}.heightMm`,
      issues,
      true,
    );
    assertFinite(container.xMm, `${containerPath}.xMm`, issues);
    assertFinite(container.yMm, `${containerPath}.yMm`, issues);
    assertPositive(container.gapMm, `${containerPath}.gapMm`, issues);

    const padding = normalizeSpacing(container.paddingMm);
    assertNonNegativeSpacing(padding, `${containerPath}.paddingMm`, issues);

    if (container.elements.length === 0) {
      warnings.push({
        code: "EMPTY_CONTAINER",
        message: `${containerPath} has no elements.`,
        path: containerPath,
      });
    }

    container.elements.forEach((element, elementIndex) => {
      validateElement(
        element,
        `${containerPath}.elements[${elementIndex}]`,
        usedElementIds,
        referencedVariablePaths,
        requiredSourcePaths,
        issues,
      );
    });
  });
}

export function validateTemplate(
  template: LabelTemplateV2,
): TemplateValidationResult {
  const issues: string[] = [];
  const warnings: EngineWarning[] = [];
  const referencedVariablePaths = new Set<string>();
  const requiredSourcePaths = new Set<string>();

  if (!template.id) {
    issues.push("template.id must be a non-empty string.");
  }

  if (!template.name) {
    issues.push("template.name must be a non-empty string.");
  }

  if (!Array.isArray(template.variables)) {
    issues.push("template.variables must be an array.");
  }

  assertPositive(template.canvas.widthMm, "canvas.widthMm", issues, true);
  assertPositive(template.canvas.heightMm, "canvas.heightMm", issues, true);

  const canvasPadding = normalizeSpacing(template.canvas.paddingMm);
  assertNonNegativeSpacing(canvasPadding, "canvas.paddingMm", issues);

  if (template.containers.length === 0) {
    warnings.push({
      code: "EMPTY_TEMPLATE",
      message:
        "Template has no containers; output ZPL will include only setup commands.",
      path: "containers",
    });
  }

  const { declaredPaths, requiredDeclaredPaths } = validateVariables(
    Array.isArray(template.variables) ? template.variables : [],
    issues,
  );

  validateContainers(
    template,
    referencedVariablePaths,
    requiredSourcePaths,
    warnings,
    issues,
  );

  for (const path of referencedVariablePaths) {
    if (!declaredPaths.has(path)) {
      issues.push(
        `Element source '${path}' must be declared in template.variables.`,
      );
    }
  }

  for (const path of requiredSourcePaths) {
    if (!declaredPaths.has(path)) {
      continue;
    }
    if (!requiredDeclaredPaths.has(path)) {
      issues.push(
        `Element source '${path}' is required by layout but template.variables marks it as optional.`,
      );
    }
  }

  validateFontDefaults(template.defaults?.font, "defaults.font", issues);
  validateBarcodeDefaults(
    template.defaults?.barcode,
    "defaults.barcode",
    issues,
  );

  if (issues.length > 0) {
    throw new TemplateValidationError(issues);
  }

  return {
    requiredVariables: Array.from(requiredDeclaredPaths).sort(),
    warnings,
  };
}
